import { useIsMobile } from '@/hooks/useIsMobile';
import { useIsLandscape } from '@/hooks/useIsLandscape';
import { ProfileCircle } from './ProfileCircle';
import { ItemsDetails } from './ItemsDetails';
import { BottomSheetPlayerDetails } from './BottomSheetPlayerDetails';
import { OffersPane } from '@/components/OffersPane/OffersPane';
import { isMultiOfferCart } from '@/utils/offers';
import { useContext } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { useIsBottomSheet } from '@/context/BottomSheetContext';
import { cn } from '@/utils/cn';

export const PlayerDetails = ({
  showPurchedItems,
  handleShowPurchedItems,
}: {
  showPurchedItems: boolean;
  handleShowPurchedItems: () => void;
}) => {
  const { offer, offers, template, subscription } = useContext(CheckoutContext);
  const bottomSheet = useIsBottomSheet();
  const isMobile = useIsMobile();
  const isLandscape = useIsLandscape();
  const { showItemsToggle, compactDetailsLayout } = template.features;

  const shouldShowToggle = showItemsToggle && (isMobile || compactDetailsLayout) && !subscription;
  const isMultiOfferCartCheckout = isMultiOfferCart(offers);
  // Landscape mobile flattens the pane: the publisher logo moves INTO the pane
  // header and the collapse chevron is dropped (Figma 9007:14573). Portrait and
  // desktop keep the collapsible variant with the outer `ProfileCircle`.
  const offersPaneVariant = isLandscape ? 'expanded' : 'collapsible';
  const offerName = offer?.displayName ?? offer?.name ?? '';
  const detailsToggle = shouldShowToggle ? (
    <ItemsDetails showPurchedItems={showPurchedItems} handleShowPurchedItems={handleShowPurchedItems} />
  ) : null;

  if (bottomSheet) {
    return <BottomSheetPlayerDetails detailsToggle={detailsToggle} />;
  }

  if (isMultiOfferCartCheckout) {
    return (
      <div className={cn('mb-3 flex w-full flex-col gap-3', compactDetailsLayout ? 'md:w-full' : 'md:w-5/6')}>
        {offersPaneVariant === 'collapsible' && <ProfileCircle />}
        <OffersPane lineItems={offers} variant={offersPaneVariant} />
      </div>
    );
  }

  return (
    <div
      className={cn('flex flex-row items-center justify-between md:w-5/6 mb-3', compactDetailsLayout && 'md:w-full')}
    >
      <div className="flex flex-row items-center gap-3 min-w-0 flex-1">
        <ProfileCircle />
        <div className="font-semibold truncate min-w-0">{offerName}</div>
      </div>
      {detailsToggle}
    </div>
  );
};
